/**
 * LLM Fix Assistant GraphQL Operations
 *
 * FERP CRITICAL: AI-powered fix recommendations for runtime validation errors
 * Domain-agnostic validation error fixing with human-in-the-loop
 *
 * Principles:
 * - All fix logic from backend (LLMFixAssistant service)
 * - Human approval required for all fixes
 * - Re-validation mandatory after applying fixes
 * - Repository pattern strict enforcement
 * - Complete audit trail for decisions
 * - Confidence scoring displayed to user
 */

import { gql } from '@apollo/client';

// ============================================================================
// GraphQL Mutations
// ============================================================================

export const REQUEST_LLM_FIX = gql`
  mutation RequestLLMFix($input: RequestLLMFixInput!) {
    requestLLMFix(input: $input) {
      recommendation_id
      original_template_id
      suggested_fixes {
        field_id
        step_id
        fix_type
        before
        after
        reasoning
        confidence
      }
      confidence
      reasoning
      diff
      status
      created_at
      created_by
    }
  }
`;

export const APPLY_LLM_FIX = gql`
  mutation ApplyLLMFix($input: ApplyLLMFixInput!) {
    applyLLMFix(input: $input) {
      template_id
      fixes_applied
      validation_passed
      remaining_violations
      updated_template
    }
  }
`;

export const REJECT_LLM_FIX = gql`
  mutation RejectLLMFix($input: RejectLLMFixInput!) {
    rejectLLMFix(input: $input)
  }
`;

// ============================================================================
// GraphQL Queries
// ============================================================================

export const GET_LLM_FIX_RECOMMENDATION = gql`
  query GetLLMFixRecommendation(
    $recommendationId: ID!
    $templateId: ID!
    $tenantId: ID!
  ) {
    getLLMFixRecommendation(
      recommendationId: $recommendationId
      templateId: $templateId
      tenantId: $tenantId
    ) {
      recommendation_id
      original_template_id
      suggested_fixes {
        field_id
        step_id
        fix_type
        before
        after
        reasoning
        confidence
      }
      confidence
      reasoning
      diff
      status
      created_at
      created_by
      applied_at
      applied_by
    }
  }
`;

export const LIST_LLM_FIX_RECOMMENDATIONS = gql`
  query ListLLMFixRecommendations($templateId: ID!, $tenantId: ID!) {
    listLLMFixRecommendations(templateId: $templateId, tenantId: $tenantId) {
      recommendation_id
      confidence
      status
      created_at
    }
  }
`;

// ============================================================================
// TypeScript Types
// ============================================================================

export type FixType = 
  | 'remove_field' 
  | 'change_component' 
  | 'add_subfields' 
  | 'fix_props' 
  | 'add_label';

export interface TemplateFix {
  field_id: string;
  step_id: string;
  fix_type: FixType;
  before: string; // JSON
  after: string; // JSON
  reasoning: string;
  confidence: number;
}

export type RecommendationStatus = 
  | 'pending' 
  | 'accepted' 
  | 'rejected' 
  | 'failed_revalidation';

export interface LLMFixRecommendation {
  recommendation_id: string;
  original_template_id: string;
  suggested_fixes: TemplateFix[];
  confidence: number;
  reasoning: string;
  diff: string;
  status: RecommendationStatus;
  created_at: string;
  created_by: string;
  applied_at?: string;
  applied_by?: string;
}

export interface ApplyFixResult {
  template_id: string;
  fixes_applied: number;
  validation_passed: boolean;
  remaining_violations: string[];
  updated_template?: string;
}

// ============================================================================
// Input Types (for TypeScript type checking)
// ============================================================================

export interface RequestLLMFixInput {
  template_id: string;
  tenant_id: string;
  requested_by: string;
}

export interface ApplyLLMFixInput {
  recommendation_id: string;
  template_id: string;
  tenant_id: string;
  applied_by: string;
  fix_indices?: number[];
}

export interface RejectLLMFixInput {
  recommendation_id: string;
  template_id: string;
  tenant_id: string;
  rejection_reason?: string;
}
